import { useState } from 'react';

import {
  Button,
  Center,
  Flex,
  HStack,
  PinInput,
  PinInputField,
  Spinner,
  Text,
  useBoolean,
  VStack,
} from '@chakra-ui/react';

import {
  CheckIndividualEmailResponse,
  getError,
  Individual,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import useCheckIndividualEmail from 'app/hooks/useCheckIndividualEmail';
import useVerifyIndividualEmail from 'app/hooks/useVerifyIndividualEmail';

import { useAuthenticationStore } from 'app/store';

import { AuthenticationStepProps } from '../types';

function VerifyEmail({ onSetStep }: AuthenticationStepProps) {
  const authenticationData = useAuthenticationStore((state) => state.authenticationData);
  const setAuthenticationData = useAuthenticationStore((state) => state.setAuthenticationData);

  const [verifyError, setVerifyError] = useState('');
  const [resendMessage, setResendMessage] = useState('Code not received?');
  const [isResendDisabled, setIsResendDisabled] = useBoolean();

  const { mutate: checkEmail, isLoading: isCheckingEmail } = useCheckIndividualEmail({
    onSuccess: (data: CheckIndividualEmailResponse) =>
      setAuthenticationData({ ...authenticationData, transactionId: data.transactionId }),
    onError: () => setVerifyError('Unable to resend verification code. Please try again later.'),
  });

  const { mutate: verifyEmail, isLoading: isVerifyingEmail } = useVerifyIndividualEmail({
    onSuccess: (data: Individual) => {
      setAuthenticationData({ ...authenticationData, signUpToken: data.signUpToken });
      onSetStep('userDetails');
    },
    onError: (error) => {
      const baseError = getError(error);

      if (baseError.status === 403) {
        setVerifyError('Wrong code. Please try again');
      } else {
        setVerifyError('Something went wrong. Please try again');
      }
    },
  });

  const handleVerifyEmail = (code: string) => {
    verifyEmail({
      email: authenticationData.email,
      transactionId: authenticationData.transactionId,
      code,
    });
  };

  const handleResendCode = () => {
    checkEmail({ email: authenticationData.email });

    setIsResendDisabled.on();
    setResendMessage('A new verification code was sent!');
    setTimeout(() => {
      setResendMessage('Code not received?');
      setIsResendDisabled.off();
    }, 120000);
  };

  return (
    <Flex flexDir="column" mt="35px">
      <Button
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={() => onSetStep('checkEmail')}
      >
        Back
      </Button>
      <Text as="h2" textStyle="h2" color="brand.darkPurple" mt="25px">
        Email verification
      </Text>
      <Text as="h5" textStyle="h5Medium" color="brand.mutedPurple" mt="15px" mb="35px">
        Please enter the verification code we’ve sent to your work email:{' '}
        <Text as="span" color="brand.darkPurple">
          {authenticationData.email}
        </Text>
      </Text>
      {isVerifyingEmail ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" emptyColor="brand.purpleAlpha30" size="xl" />
        </Center>
      ) : (
        <VStack spacing="10px">
          <HStack spacing="20px" justify="center">
            <PinInput
              placeholder=""
              onComplete={handleVerifyEmail}
              size="lg"
              isInvalid={!!verifyError}
            >
              <PinInputField />
              <PinInputField />
              <PinInputField />
              <PinInputField />
            </PinInput>
          </HStack>
          {verifyError && (
            <Text textStyle="smallMedium" color="system.error">
              {verifyError}
            </Text>
          )}
        </VStack>
      )}

      <VStack mt="35px" spacing={0}>
        <Text textStyle="smallRegular" color="brand.darkGray">
          {resendMessage}
        </Text>
        <Button
          size="sm"
          variant="link"
          onClick={handleResendCode}
          isDisabled={isResendDisabled}
          isLoading={isCheckingEmail}
        >
          Resend code
        </Button>
      </VStack>
    </Flex>
  );
}

export default VerifyEmail;
